import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowsClockwise, Sparkle, X } from "@phosphor-icons/react";
import { APP_VERSION, compareVersions, performUpdate, getPlatform } from "@/lib/appVersion";
import { API as API_BASE } from "@/lib/api";

const POLL_MS = 10 * 60 * 1000; // 10 minutes
const SKIP_STORAGE_KEY = "zf_update_skip_v"; // per-optional-version dismiss

const apiBase = API_BASE;

/**
 * UpdatePrompt — checks the server's latest published version against the
 * bundled APP_VERSION. Shows a modal when an update is available.
 *
 * Rules:
 *  - mandatory=true OR client < min_supported_version → blocking modal (no close)
 *  - otherwise: dismissable; remembers the dismissed version in localStorage
 *    so we don't nag every poll (but it WILL reappear after a new release).
 *
 * Mounted globally in App.js so it covers both /portal and /admin.
 */
export default function UpdatePrompt() {
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  const check = useCallback(async () => {
    try {
      // Bust caches/SW intermediaries so we never see a stale version doc
      const { data } = await axios.get(`${apiBase}/public/app-version`, {
        params: { _ts: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      });
      setMeta(data);
      const latest = data?.version;
      if (!latest) return;
      const isOutdated = compareVersions(APP_VERSION, latest) < 0;
      const belowMin = data?.min_supported_version
        && compareVersions(APP_VERSION, data.min_supported_version) < 0;
      const isMandatory = !!data?.mandatory || !!belowMin;
      const dismissed = localStorage.getItem(SKIP_STORAGE_KEY);
      if (isOutdated && (isMandatory || dismissed !== latest)) {
        setOpen(true);
      } else if (!isOutdated && open) {
        setOpen(false);
      }
    } catch { /* offline or backend down → keep silent */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    check();
    const i = setInterval(check, POLL_MS);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      clearInterval(i);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [check]);

  if (!meta) return null;
  const belowMin = meta.min_supported_version && compareVersions(APP_VERSION, meta.min_supported_version) < 0;
  const blocking = !!meta.mandatory || !!belowMin;

  const handleUpdate = async () => {
    setUpdating(true);
    await performUpdate(meta);
    // performUpdate triggers navigation; reset state in case it doesn't.
    setTimeout(() => setUpdating(false), 4000);
  };

  const handleSkip = () => {
    try { localStorage.setItem(SKIP_STORAGE_KEY, meta.version); } catch { /* noop */ }
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!blocking) (v ? setOpen(true) : handleSkip()); }}>
      <DialogContent
        hideClose={blocking}
        className="max-w-sm p-0 overflow-hidden border-zinc-200"
        onInteractOutside={(e) => blocking && e.preventDefault()}
        onEscapeKeyDown={(e) => blocking && e.preventDefault()}
        data-testid="update-prompt"
      >
        <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 text-white p-6">
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/10 blur-2xl"/>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 text-[10px] tracking-widest uppercase font-semibold backdrop-blur">
              <Sparkle size={11} weight="fill"/> {blocking ? "Atualização obrigatória" : "Nova versão"}
            </div>
            <div className="font-display text-2xl font-semibold tracking-tight mt-3">
              Nova versão disponível!
            </div>
            <p className="text-sm text-white/85 mt-2 leading-relaxed">
              {meta.message || "Atualizamos o Zeferius Pay com melhorias importantes."}
            </p>
          </div>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Box label="Sua versão" value={APP_VERSION} muted/>
            <Box label="Nova versão" value={meta.version} accent/>
          </div>
          {blocking && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5">
              Esta atualização é obrigatória — atualize para continuar usando o app.
            </div>
          )}
          <Button
            onClick={handleUpdate}
            disabled={updating}
            data-testid="update-now-btn"
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base"
          >
            <ArrowsClockwise size={18} weight="bold" className={`mr-2 ${updating ? "animate-spin" : ""}`}/>
            {updating ? "Atualizando..." : "Atualizar agora"}
          </Button>
          {!blocking && (
            <button
              onClick={handleSkip}
              data-testid="update-skip-btn"
              className="w-full text-sm text-zinc-500 hover:text-zinc-700 py-1 font-medium"
            >
              Lembrar mais tarde
            </button>
          )}
          <div className="text-[10px] text-zinc-400 text-center pt-1">
            Plataforma detectada: <span className="font-mono uppercase">{getPlatform()}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Box = ({ label, value, muted, accent }) => (
  <div className={`rounded-xl border p-3 ${accent ? "bg-blue-50 border-blue-200" : "border-zinc-200"}`}>
    <div className={`text-[10px] tracking-widest uppercase font-semibold ${muted ? "text-zinc-400" : "text-zinc-500"}`}>{label}</div>
    <div className={`font-display text-lg font-bold tab-nums mt-1 ${accent ? "text-blue-700" : "text-zinc-900"}`}>{value}</div>
  </div>
);
